import React, { useState, useEffect, useRef } from 'react';
import {
  useClick,
  useInteractions,
  useFloating,
  useDismiss,
  autoPlacement,
  FloatingPortal,
  FloatingArrow,
  arrow,
  offset,
  flip,
  shift
} from '@floating-ui/react';
import clsx from 'clsx';

export function Dropdown({
  button,
  children,
  hideOnClick,
  isOpened: externalIsOpened = false,
  setIsOpened: externalSetIsOpened,
  portalId, // https://floating-ui.com/docs/floatingportal#id,
  zIndex = 'z-[10]',
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(externalIsOpened);
  const arrowRef = useRef(null);

  useEffect(() => {
    if (!!externalSetIsOpened) {
      setIsOpen(externalIsOpened);
    }
  }, [externalIsOpened, externalSetIsOpened]);

  const handleContentClick = e => {
    e.preventDefault();

    if (hideOnClick && Array.isArray(hideOnClick) && hideOnClick.length >= 1) {
      const matches = hideOnClick.find(selector => e.target.closest(selector));
      if (matches) {
        if (externalSetIsOpened) {
          externalSetIsOpened(false);
        } else {
          setIsOpen(false);
        }
      }
    } else if (!!hideOnClick) {
      if (externalSetIsOpened) {
        externalSetIsOpened(false);
      } else {
        setIsOpen(false);
      }
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: externalSetIsOpened || setIsOpen,
    placement: 'bottom',
    middleware: [
      offset(10),
      flip(),
      shift(),
      arrow({
        element: arrowRef
      })
    ]
  });

  const click = useClick(context, { toggle: true });
  const dismiss = useDismiss(context, {
    outsidePress: true
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
  const arrowClasses = 'fill-white z-[100] [&>path:first-of-type]:stroke-slate-200 z-[2]';

  // Create a wrapper for child components with event listeners
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { onClick: handleContentClick });
    }
    return child;
  });

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={clsx('cursor-pointer truncate', disabled && 'pointer-events-none opacity-50')}
      >
        {button}
      </div>
      {!disabled && isOpen && (
        <FloatingPortal id={portalId}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={zIndex}
          >
            <div
              className={clsx(
                'bg-white rounded-lg ring-1 ring-solid ring-slate-200 shadow-lg relative z-[1] overflow-hidden',
                disabled && 'pointer-events-none opacity-50'
              )}
            >
              {childrenWithProps}
            </div>
            <FloatingArrow
              width={16}
              height={10}
              ref={arrowRef}
              context={context}
              className={arrowClasses}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
